<template>
    <div class="register-page">
        <!-- Animated background -->
        <div class="bg-grid"></div>
        <div class="bg-glow bg-glow-1"></div>
        <div class="bg-glow bg-glow-2"></div>
        
        <div class="register-container">
            <!-- Success View -->
            <div v-if="success" class="register-card success-card">
                <div class="success-icon-wrapper">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                        <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                </div>
                <h2>Registration Successful!</h2>
                <p class="success-message">{{ success }}</p>
                <router-link to="/login" class="btn-primary">
                    Go to Login
                </router-link>
            </div>

            <!-- Registration Form -->
            <div v-else class="register-card">
                <!-- Logo and branding -->
                <div class="brand-section">
                    <img src="@/assets/segofy-logo.png" alt="Segofy" class="logo" />
                    <p class="tagline">Create your account</p>
                </div>

                <form name="register-form" @submit.prevent="handleSubmit">
                    <!-- Name row -->
                    <div class="input-row">
                        <div class="input-group">
                            <label for="firstName">First Name *</label>
                            <div class="input-wrapper">
                                <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                    <circle cx="12" cy="7" r="4"/>
                                </svg>
                                <input 
                                    type="text" 
                                    id="firstName" 
                                    v-model="input.firstName"
                                    placeholder="John"
                                    required
                                />
                            </div>
                        </div>
                        <div class="input-group">
                            <label for="lastName">Last Name *</label>
                            <div class="input-wrapper">
                                <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                    <circle cx="12" cy="7" r="4"/>
                                </svg>
                                <input 
                                    type="text" 
                                    id="lastName" 
                                    v-model="input.lastName"
                                    placeholder="Doe"
                                    required
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Insertion field -->
                    <div class="input-group">
                        <label for="insertion">Insertion</label>
                        <div class="input-wrapper">
                            <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                <circle cx="12" cy="7" r="4"/>
                            </svg>
                            <input 
                                type="text" 
                                id="insertion" 
                                v-model="input.insertion"
                                placeholder="van, de, etc."
                            />
                        </div>
                    </div>

                    <!-- Email field -->
                    <div class="input-group">
                        <label for="email">Email *</label>
                        <div class="input-wrapper">
                            <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                                <polyline points="22,6 12,13 2,6"/>
                            </svg>
                            <input 
                                type="email" 
                                id="email" 
                                v-model="input.email"
                                autocomplete="email"
                                placeholder="you@example.com"
                                required
                            />
                        </div>
                    </div>

                    <!-- Phone field -->
                    <div class="input-group">
                        <label for="mobilePhone">Mobile Phone</label>
                        <div class="phone-input-wrapper">
                            <select v-model="input.countryCode" class="country-select">
                                <option v-for="country in countryCodes" :key="country.code" :value="country.code">
                                    {{ country.flag }} {{ country.code }}
                                </option>
                            </select>
                            <div class="input-wrapper phone-input-field">
                                <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                                </svg>
                                <input 
                                    type="tel" 
                                    id="mobilePhone" 
                                    v-model="input.mobilePhone"
                                    placeholder="123 456 7890"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Password field -->
                    <div class="input-group">
                        <label for="password">Password *</label>
                        <div class="input-wrapper">
                            <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                            </svg>
                            <input 
                                type="password" 
                                id="password" 
                                v-model="input.password"
                                placeholder="••••••••"
                                required
                            />
                        </div>
                        <!-- Password requirements -->
                        <div class="password-requirements">
                            <div class="requirement" :class="{ valid: hasValidLength }">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <polyline v-if="hasValidLength" points="20 6 9 17 4 12"/>
                                    <line v-else x1="18" y1="6" x2="6" y2="18"/>
                                    <line v-if="!hasValidLength" x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                                <span>9-50 characters</span>
                            </div>
                            <div class="requirement" :class="{ valid: hasLetter }">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <polyline v-if="hasLetter" points="20 6 9 17 4 12"/>
                                    <line v-else x1="18" y1="6" x2="6" y2="18"/>
                                    <line v-if="!hasLetter" x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                                <span>One letter</span>
                            </div>
                            <div class="requirement" :class="{ valid: hasNumber }">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <polyline v-if="hasNumber" points="20 6 9 17 4 12"/>
                                    <line v-else x1="18" y1="6" x2="6" y2="18"/>
                                    <line v-if="!hasNumber" x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                                <span>One number</span>
                            </div>
                            <div class="requirement" :class="{ valid: hasSymbol }">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <polyline v-if="hasSymbol" points="20 6 9 17 4 12"/>
                                    <line v-else x1="18" y1="6" x2="6" y2="18"/>
                                    <line v-if="!hasSymbol" x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                                <span>One symbol</span>
                            </div>
                        </div>
                    </div>

                    <!-- Error message -->
                    <Transition name="fade">
                        <div v-if="error" class="message error-message">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="15" y1="9" x2="9" y2="15"/>
                                <line x1="9" y1="9" x2="15" y2="15"/>
                            </svg>
                            <span>{{ error }}</span>
                        </div>
                    </Transition>

                    <!-- Register button -->
                    <button class="btn-primary" type="submit" :disabled="loading">
                        <span v-if="loading" class="spinner"></span>
                        <span v-else>Create Account</span>
                    </button>

                    <!-- Divider -->
                    <div class="divider">
                        <span>or continue with</span>
                    </div>

                    <!-- Google button -->
                    <button type="button" class="btn-google" @click="handleGoogleRegister" :disabled="loading">
                        <svg class="google-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                        </svg>
                        <span>Google</span>
                    </button>
                </form>

                <!-- Login link -->
                <div class="login-link">
                    <span>Already have an account?</span>
                    <router-link to="/login">Sign in</router-link>
                </div>
            </div>

            <!-- Footer -->
            <p class="footer-text">
                Secure registration protected by industry-standard encryption
            </p>
        </div>
    </div>
</template>

<script>
import { api } from '../utils/api'
import { countryPhoneCodes } from '../utils/countryPhoneCodes'

export default {
    name: 'RegisterView',
    inject: ['env'],
    data() {
        return {
            input: {
                firstName: "",
                lastName: "",
                insertion: "",
                email: "",
                mobilePhone: "",
                countryCode: "+1",
                password: ""
            },
            loading: false,
            error: null,
            success: null,
            countryCodes: countryPhoneCodes
        }
    },
    computed: {
        hasLetter() {
            return /[a-zA-Z]/.test(this.input.password)
        },
        hasNumber() {
            return /\d/.test(this.input.password)
        },
        hasSymbol() {
            return /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(this.input.password)
        },
        hasValidLength() {
            return this.input.password.length >= 9 && this.input.password.length <= 50
        }
    },
    created() {
        const token = (() => { try { return localStorage.getItem('fic') } catch (_) { return null } })()
        if (token) {
            window.location.replace('/domains')
        }
    },
    methods: {
        validateEmail(email) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            return emailRegex.test(email) && email.length <= 200
        },
        
        validatePassword(password) {
            if (password.length < 9) {
                return { valid: false, message: 'Password must be at least 9 characters long.' }
            }
            if (password.length > 50) {
                return { valid: false, message: 'Password must be no more than 50 characters long.' }
            }
            if (!/[a-zA-Z]/.test(password)) {
                return { valid: false, message: 'Password must contain at least one letter.' }
            }
            if (!/\d/.test(password)) {
                return { valid: false, message: 'Password must contain at least one number.' }
            }
            if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
                return { valid: false, message: 'Password must contain at least one symbol (!@#$%^&*()_+-=[]{}|;:,.<>?).' }
            }
            return { valid: true }
        },
        
        async handleSubmit() {
            this.loading = true
            this.error = null
            this.success = null

            if (!this.input.firstName.trim() || !this.input.lastName.trim() || !this.input.email.trim() || !this.input.password.trim()) {
                this.error = 'Please fill in all required fields.'
                this.loading = false
                return
            }

            if (!this.validateEmail(this.input.email)) {
                this.error = 'Please enter a valid email address (max 200 characters).'
                this.loading = false
                return
            }

            const passwordValidation = this.validatePassword(this.input.password)
            if (!passwordValidation.valid) {
                this.error = passwordValidation.message
                this.loading = false
                return
            }

            try {
                const registrationData = {
                    firstName: this.input.firstName.trim(),
                    lastName: this.input.lastName.trim(),
                    email: this.input.email.trim(),
                    password: this.input.password
                }

                if (this.input.insertion.trim()) {
                    registrationData.insertion = this.input.insertion.trim()
                }
                if (this.input.mobilePhone.trim()) {
                    registrationData.mobilePhone = this.input.countryCode + this.input.mobilePhone.trim()
                }

                await api('/register', {
                    method: 'POST',
                    body: JSON.stringify(registrationData)
                }, this.$router, { skipAuthCheck: true })

                this.success = 'Please check your email to continue with the registration process.'

            } catch (error) {
                this.error = error.code || 'Something went wrong please contact support'
                console.error('Registration error:', error)
            } finally {
                this.loading = false
            }
        },

        async handleGoogleRegister() {
            this.loading = true
            this.error = null

            try {
                const response = await api('/auth/google/login', {
                    method: 'GET'
                }, this.$router, { skipAuthCheck: true })

                if (response && response.authUrl && response.state) {
                    try {
                        sessionStorage.setItem('oauth_state', response.state)
                    } catch (storageError) {
                        console.error('Failed to store OAuth state:', storageError)
                        this.error = 'Failed to store session data. Please check browser settings.'
                        this.loading = false
                        return
                    }
                    window.location.href = response.authUrl
                } else {
                    throw new Error('No auth URL or state received from server')
                }
            } catch (error) {
                this.error = error.code || 'Something went wrong please contact support'
                console.error('Google authentication error:', error)
                this.loading = false
            }
        }
    }
}
</script>

<style scoped>
.register-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    inset: 0;
    overflow-y: auto;
    background: linear-gradient(135deg, #0a0a0a 0%, #151520 50%, #0d0d15 100%);
}

/* Animated grid background */
.bg-grid {
    position: fixed;
    inset: 0;
    background-image: 
        linear-gradient(rgba(60, 98, 230, 0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(60, 98, 230, 0.03) 1px, transparent 1px);
    background-size: 60px 60px;
    mask-image: radial-gradient(ellipse at center, black 0%, transparent 75%);
    animation: gridPulse 8s ease-in-out infinite;
}

@keyframes gridPulse {
    0%, 100% { opacity: 0.5; }
    50% { opacity: 1; }
}

/* Floating glow effects */
.bg-glow {
    position: fixed;
    border-radius: 50%;
    filter: blur(100px);
    opacity: 0.4;
    animation: float 15s ease-in-out infinite;
}

.bg-glow-1 {
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(60, 98, 230, 0.3) 0%, transparent 70%);
    top: -200px;
    right: -100px;
    animation-delay: 0s;
}

.bg-glow-2 {
    width: 400px;
    height: 400px;
    background: radial-gradient(circle, rgba(94, 194, 105, 0.2) 0%, transparent 70%);
    bottom: -150px;
    left: -100px;
    animation-delay: -7s;
}

@keyframes float {
    0%, 100% { transform: translate(0, 0) scale(1); }
    33% { transform: translate(30px, -30px) scale(1.05); }
    66% { transform: translate(-20px, 20px) scale(0.95); }
}

.register-container {
    position: relative;
    z-index: 10;
    width: 100%;
    max-width: 480px;
    padding: 20px;
    margin: 40px 0;
    animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.register-card {
    background: rgba(24, 24, 25, 0.8);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 20px;
    padding: 36px 32px;
    box-shadow: 
        0 25px 50px -12px rgba(0, 0, 0, 0.5),
        0 0 0 1px rgba(255, 255, 255, 0.05) inset;
}

/* Success card */
.success-card {
    text-align: center;
    padding: 48px 36px;
}

.success-icon-wrapper {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: rgba(94, 194, 105, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 24px;
}

.success-icon-wrapper svg {
    width: 40px;
    height: 40px;
    color: var(--success-color);
}

.success-card h2 {
    color: var(--success-color);
    margin: 0 0 12px;
    font-size: 1.5rem;
}

.success-card .success-message {
    color: var(--text-secondary);
    margin: 0 0 28px;
    font-size: 1rem;
    line-height: 1.6;
}

.success-card .btn-primary {
    display: inline-flex;
    text-decoration: none;
}

/* Brand section */
.brand-section {
    text-align: center;
    margin-bottom: 32px;
}

.logo {
    height: 36px;
    width: auto;
    margin-bottom: 12px;
}

.tagline {
    color: var(--text-secondary);
    font-size: 0.9rem;
    margin: 0;
    letter-spacing: 0.02em;
}

/* Form elements */
.input-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

.input-group {
    margin-bottom: 16px;
}

.input-group label {
    display: block;
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--text-secondary);
    margin-bottom: 8px;
    letter-spacing: 0.02em;
}

.input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.input-icon {
    position: absolute;
    left: 14px;
    width: 18px;
    height: 18px;
    color: var(--text-muted);
    pointer-events: none;
    transition: color 0.2s ease;
}

.input-wrapper:focus-within .input-icon {
    color: var(--accent-color);
}

.input-group input {
    width: 100%;
    padding: 14px 14px 14px 46px;
    font-size: 0.95rem;
    font-family: inherit;
    color: var(--text-primary);
    background: rgba(61, 61, 61, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    outline: none;
    transition: all 0.2s ease;
}

.input-group input::placeholder {
    color: var(--text-muted);
}

.input-group input:hover {
    border-color: rgba(255, 255, 255, 0.15);
    background: rgba(61, 61, 61, 0.4);
}

.input-group input:focus {
    border-color: var(--accent-color);
    background: rgba(61, 61, 61, 0.5);
    box-shadow: 0 0 0 3px rgba(60, 98, 230, 0.15);
}

/* Phone input */
.phone-input-wrapper {
    display: flex;
    gap: 8px;
}

.country-select {
    flex: 0 0 90px;
    padding: 14px 8px;
    font-size: 0.9rem;
    font-family: inherit;
    color: var(--text-primary);
    background: rgba(61, 61, 61, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    outline: none;
    transition: all 0.2s ease;
    cursor: pointer;
}

.country-select:hover {
    border-color: rgba(255, 255, 255, 0.15);
    background: rgba(61, 61, 61, 0.4);
}

.country-select:focus {
    border-color: var(--accent-color);
    background: rgba(61, 61, 61, 0.5);
    box-shadow: 0 0 0 3px rgba(60, 98, 230, 0.15);
}

.phone-input-field {
    flex: 1;
}

/* Password requirements */
.password-requirements {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px 12px;
    margin-top: 10px;
    padding: 12px;
    background: rgba(61, 61, 61, 0.2);
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.05);
}

.requirement {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.75rem;
    color: var(--text-muted);
    transition: color 0.2s ease;
}

.requirement svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
}

.requirement.valid {
    color: var(--success-color);
}

/* Messages */
.message {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 14px 16px;
    border-radius: 12px;
    margin-bottom: 16px;
    font-size: 0.9rem;
    line-height: 1.5;
}

.message svg {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    margin-top: 2px;
}

.error-message {
    background: rgba(221, 82, 76, 0.1);
    border: 1px solid rgba(221, 82, 76, 0.2);
    color: var(--error-color);
}

/* Buttons */
.btn-primary {
    width: 100%;
    padding: 14px 24px;
    font-size: 1rem;
    font-weight: 600;
    font-family: inherit;
    color: white;
    background: linear-gradient(135deg, var(--accent-color) 0%, #2a4bc4 100%);
    border: none;
    border-radius: 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.2s ease;
    box-shadow: 0 4px 15px rgba(60, 98, 230, 0.3);
}

.btn-primary:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(60, 98, 230, 0.4);
}

.btn-primary:active:not(:disabled) {
    transform: translateY(0);
}

.btn-primary:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

/* Spinner */
.spinner {
    width: 20px;
    height: 20px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

/* Divider */
.divider {
    display: flex;
    align-items: center;
    gap: 16px;
    margin: 20px 0;
}

.divider::before,
.divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
}

.divider span {
    font-size: 0.8rem;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.1em;
    white-space: nowrap;
}

/* Google button */
.btn-google {
    width: 100%;
    padding: 14px 24px;
    font-size: 0.95rem;
    font-weight: 500;
    font-family: inherit;
    color: var(--text-primary);
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    transition: all 0.2s ease;
}

.btn-google:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.15);
}

.btn-google:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.google-icon {
    width: 20px;
    height: 20px;
}

/* Login link */
.login-link {
    text-align: center;
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
}

.login-link span {
    color: var(--text-secondary);
}

.login-link a {
    color: var(--accent-color);
    text-decoration: none;
    font-weight: 500;
    transition: color 0.2s ease;
}

.login-link a:hover {
    color: #5a7ff0;
}

/* Footer text */
.footer-text {
    text-align: center;
    margin-top: 24px;
    font-size: 0.75rem;
    color: var(--text-muted);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
    transition: all 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}

/* Responsive */
@media (max-width: 768px) {
    .register-container {
        padding: 12px;
        margin: 20px 0;
    }

    .register-card {
        padding: 28px 24px;
        border-radius: 16px;
    }

    .brand-section {
        margin-bottom: 24px;
    }

    .logo {
        height: 32px;
    }

    .tagline {
        font-size: 0.85rem;
    }

    .input-row {
        grid-template-columns: 1fr;
        gap: 0;
    }

    .input-group {
        margin-bottom: 14px;
    }

    .input-group input {
        padding: 12px 12px 12px 42px;
    }

    .country-select {
        padding: 12px 8px;
    }

    .password-requirements {
        grid-template-columns: 1fr;
        gap: 4px;
    }

    .btn-primary,
    .btn-google {
        padding: 12px 20px;
    }

    .divider {
        margin: 16px 0;
    }

    .login-link {
        margin-top: 18px;
        padding-top: 16px;
    }

    .footer-text {
        margin-top: 16px;
        font-size: 0.7rem;
    }

    .bg-glow-1 {
        width: 300px;
        height: 300px;
    }

    .bg-glow-2 {
        width: 250px;
        height: 250px;
    }
}

@media (max-width: 480px) {
    .register-container {
        padding: 8px;
    }

    .register-card {
        padding: 24px 20px;
    }

    .brand-section {
        margin-bottom: 20px;
    }

    .logo {
        height: 28px;
        margin-bottom: 8px;
    }

    .tagline {
        font-size: 0.8rem;
    }

    .input-group label {
        font-size: 0.8rem;
        margin-bottom: 6px;
    }

    .message {
        padding: 12px 14px;
        font-size: 0.85rem;
    }

    .login-link {
        font-size: 0.85rem;
    }

    .footer-text {
        margin-top: 12px;
    }

    .success-card {
        padding: 36px 24px;
    }

    .success-icon-wrapper {
        width: 64px;
        height: 64px;
        margin-bottom: 20px;
    }

    .success-icon-wrapper svg {
        width: 32px;
        height: 32px;
    }

    .success-card h2 {
        font-size: 1.25rem;
    }
}
</style>
